/* FS mapping — moved out of pages/FsMapping.jsx in split step 6e (28-Oct-2026), unchanged. */
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { asArray } from "@/lib/asArray";

/* ------------------------------------------------------------------
 * Custom FS + Note code dialogs. Any user at the firm can create a
 * custom line; codes are auto-assigned in reserved slots per the
 * central taxonomy convention. Tenant-scoped only.
 * ------------------------------------------------------------------ */
function CustomFsDialog({ open, onOpenChange, onCreated }) {
  const { t } = useI18n();
  const PLACEMENTS = [
    { v: "SFP non-current assets",       label: t("fsMappingPage.customFs.placements.ncAssets"),  hint: t("fsMappingPage.customFs.hints.ncAssets") },
    { v: "SFP current assets",           label: t("fsMappingPage.customFs.placements.curAssets"), hint: t("fsMappingPage.customFs.hints.curAssets") },
    { v: "SFP equity",                    label: t("fsMappingPage.customFs.placements.equity"),    hint: t("fsMappingPage.customFs.hints.equity") },
    { v: "SFP non-current liabilities",  label: t("fsMappingPage.customFs.placements.ncLiab"),    hint: t("fsMappingPage.customFs.hints.ncLiab") },
    { v: "SFP current liabilities",      label: t("fsMappingPage.customFs.placements.curLiab"),   hint: t("fsMappingPage.customFs.hints.curLiab") },
    { v: "P&L",                            label: t("fsMappingPage.customFs.placements.pl"),        hint: t("fsMappingPage.customFs.hints.pl") },
  ];
  const [line, setLine] = useState("");
  const [placement, setPlacement] = useState("SFP non-current assets");
  const [plKind, setPlKind] = useState("");    // income / expense
  const [cat, setCat] = useState("");           // OP / INV / FIN / TAX
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setLine(""); setPlacement("SFP non-current assets");
      setPlKind(""); setCat("");
    }
  }, [open]);
  const isPl = placement === "P&L";
  const validPl = !isPl || (["income", "expense"].includes(plKind) &&
                              ["OP", "INV", "FIN", "TAX"].includes(cat));
  const canSubmit = line.trim().length > 0 && validPl;
  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = { line: line.trim(), placement };
      if (isPl) { body.pl_kind = plKind; body.ifrs18_category = cat; }
      const r = await api.post("/fs/custom-fs-codes", body);
      toast.success(t("fsMappingPage.customFs.createdToast").replace("{code}", r.data.code));
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.customFs.createFailed"))); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="custom-fs-dialog">
        <DialogHeader>
          <DialogTitle>{t("fsMappingPage.customFs.title")}</DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            {t("fsMappingPage.customFs.intro")}
          </p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.customFs.lineItem")}</div>
            <Input value={line} onChange={(e) => setLine(e.target.value)}
                    placeholder={t("fsMappingPage.customFs.linePlaceholder")}
                    data-testid="custom-fs-line" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.customFs.placement")}</div>
            <Select value={placement} onValueChange={setPlacement}>
              <SelectTrigger data-testid="custom-fs-placement"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLACEMENTS.map((p) => (
                  <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[10px] text-muted-foreground">
              {PLACEMENTS.find((p) => p.v === placement)?.hint}
            </div>
          </div>
          {isPl && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.customFs.incVsExp")}</div>
                <Select value={plKind} onValueChange={setPlKind}>
                  <SelectTrigger data-testid="custom-fs-plkind"><SelectValue placeholder={t("fsMappingPage.customFs.pickOne")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">{t("fsMappingPage.customFs.income")}</SelectItem>
                    <SelectItem value="expense">{t("fsMappingPage.customFs.expense")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.customFs.ifrs18cat")}</div>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger data-testid="custom-fs-cat"><SelectValue placeholder={t("fsMappingPage.customFs.pickOne")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OP">{t("fsMappingPage.customFs.operating")}</SelectItem>
                    <SelectItem value="INV">{t("fsMappingPage.customFs.investing")}</SelectItem>
                    <SelectItem value="FIN">{t("fsMappingPage.customFs.financing")}</SelectItem>
                    <SelectItem value="TAX">{t("fsMappingPage.customFs.tax")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("fsMappingPage.customFs.cancel")}</Button>
          <Button disabled={!canSubmit || saving} onClick={submit}
                  className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
                  data-testid="custom-fs-submit">
            {saving && <Loader2 className="me-1 h-3 w-3 animate-spin" />}
            {t("fsMappingPage.customFs.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CustomNoteDialog({ open, onOpenChange, fsLines, onCreated }) {
  const { t } = useI18n();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [rooted, setRooted] = useState(false);
  const [rootFs, setRootFs] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setTitle(""); setDesc(""); setRooted(false); setRootFs(""); }
  }, [open]);
  const canSubmit = title.trim() && desc.trim().length >= 15 && (!rooted || rootFs);
  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = { title: title.trim(), description: desc.trim() };
      if (rooted && rootFs) body.root_fs_code = rootFs;
      const r = await api.post("/fs/custom-note-codes", body);
      toast.success(t("fsMappingPage.customNote.createdToast").replace("{code}", r.data.code));
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) { toast.error(toErrMsg(e, t("fsMappingPage.customFs.createFailed"))); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="custom-note-dialog">
        <DialogHeader>
          <DialogTitle>{t("fsMappingPage.customNote.title")}</DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            {t("fsMappingPage.customNote.intro").split(/\{rootedCode\}|\{crossCode\}/).map((chunk, i, arr) => (
              <React.Fragment key={i}>
                {chunk}
                {i === 0 && arr.length > 1 && <code>.80-.99</code>}
                {i === 1 && arr.length > 2 && <code>N80-N89</code>}
              </React.Fragment>
            ))}
          </p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.customNote.titleField")}</div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("fsMappingPage.customNote.titlePlaceholder")}
                    data-testid="custom-note-title" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("fsMappingPage.customNote.descRequired")} <span className="text-rose-600">*</span>
              <span className="ms-1 text-[10px] normal-case text-muted-foreground/70">
                {t("fsMappingPage.customNote.descHint")}
              </span>
            </div>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
                       className="mt-1 min-h-[70px] w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                       placeholder={t("fsMappingPage.customNote.descPlaceholder")}
                       data-testid="custom-note-desc" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={rooted} onCheckedChange={(v) => setRooted(!!v)}
                       data-testid="custom-note-rooted" />
            <span className="text-sm">{t("fsMappingPage.customNote.rootUnder")}</span>
          </div>
          {rooted && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("fsMappingPage.customNote.rootCode")}</div>
              <Select value={rootFs} onValueChange={setRootFs}>
                <SelectTrigger data-testid="custom-note-rootfs"><SelectValue placeholder={t("fsMappingPage.customNote.pickFs")} /></SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {asArray(fsLines).sort((a, b) => a.code.localeCompare(b.code)).map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      <span className="font-mono text-[10px] me-2 text-muted-foreground">{l.code}</span>
                      {l.line}{l.custom ? " · Custom" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-1 text-[10px] text-muted-foreground">
                {t("fsMappingPage.customNote.autoAssignHint").replace("{code}", rootFs || "{fs_code}")}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("fsMappingPage.customNote.cancel")}</Button>
          <Button disabled={!canSubmit || saving} onClick={submit}
                  className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
                  data-testid="custom-note-submit">
            {saving && <Loader2 className="me-1 h-3 w-3 animate-spin" />}
            {t("fsMappingPage.customNote.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { CustomFsDialog, CustomNoteDialog };
